import React, { useState, useEffect } from 'react';
import { style, classes } from 'typestyle';
import { ComponentProps, inject, deps, observer } from '../../../lib/component';
import { ArtistSchema, TrackSchema } from '../../../types/schema';
import { Loading } from '../../loading/loading';
import { TrackTile } from '../molecules/TrackTile';
import { spacing } from '../../../theme/spacing';
import { px } from 'csx';
import { FavouriteButtonCompat } from '../../compat/favourite-button-compat';
import { AddRemoveCompat } from '../../compat/add-remove-compat';
import { zLabel } from '../../../theme/z';
import PlayButton from '../atoms/button/PlayButton';
import { mediaDesktop, mediaLargeTabletOnly, mediaSmallTablet } from '../utilities/mediaQueries';

interface ArtistTopTracksProps extends ComponentProps {
  artist: ArtistSchema;
  className?: string;
  headingClassName?: string;
}

const styles = {
  heading: style({
    margin: `0 0 ${spacing.DEFAULT}`,
    fontSize: px(32),
  }),
  trackList: style(
    {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: 'auto',
      gap: spacing.DEFAULT,
    },
    mediaSmallTablet({
      gridTemplateColumns: '1fr 1fr 1fr',
    }),
    mediaDesktop({
      gridTemplateColumns: '1fr 1fr',
    })
  ),
  trackTile: style({
    margin: 0,
  }),
};

export const ArtistTopTracks: React.FC<ArtistTopTracksProps> = inject(deps)(
  observer(({ artist, className, headingClassName, model: { auth }, controller: { api } }) => {
    const [topTracks, setTopTracks] = useState<TrackSchema[]>([]);
    const [loading, setisLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error>(null);

    const getTracks = async () => {
      setisLoading(true);
      try {
        const userRegion = auth.user ? auth.user.country : null;
        const tracks = await api.artist.getTracks(artist.slug, undefined, 1, userRegion);

        setTopTracks(tracks.data);
      } catch (error) {
        setError(error);
      } finally {
        setisLoading(false);
      }
    };

    useEffect(() => {
      getTracks();
    }, []);

    if (loading) return <Loading text="Loading artist top tracks" />;
    if (error || !topTracks.length) return null;

    return (
      <div>
        <h3 className={classes(styles.heading, headingClassName)}>Top tracks by {artist.name}</h3>
        <div className={classes(styles.trackList, className)}>
          {topTracks.map((track) => (
            <TrackTile key={track.identity} track={track} className={styles.trackTile} fontSize="large">
              <PlayButton track={track} />
              <FavouriteButtonCompat track={track} size={32} />
              <AddRemoveCompat track={track} size={40} className="basket" />
            </TrackTile>
          ))}
        </div>
      </div>
    );
  })
);
